import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';

import accounts from './modules/accounts';
import adLeadsReport from './modules/adLeadsReport';
import agentBots from './modules/agentBots';
import agents from './modules/agents';
import agentPauses from './modules/agentPauses';
import articles from './modules/helpCenterArticles';
import attributes from './modules/attributes';
import auditlogs from './modules/auditlogs';
import auth from './modules/auth';
import automations from './modules/automations';
import bulkActions from './modules/bulkActions';
import campaigns from './modules/campaigns';
import cannedResponse from './modules/cannedResponse';
import categories from './modules/helpCenterCategories';
import contactConversations from './modules/contactConversations';
import contactLabels from './modules/contactLabels';
import contactNotes from './modules/contactNotes';
import contacts from './modules/contacts';
import conversationLabels from './modules/conversationLabels';
import conversationMetadata from './modules/conversationMetadata';
import conversationPage from './modules/conversationPage';
import conversations from './modules/conversations';
import conversationSearch from './modules/conversationSearch';
import conversationStats from './modules/conversationStats';
import conversationTypingStatus from './modules/conversationTypingStatus';
import conversationWatchers from './modules/conversationWatchers';
import csat from './modules/csat';
import customRole from './modules/customRole';
import customViews from './modules/customViews';
import dashboardApps from './modules/dashboardApps';
import draftMessages from './modules/draftMessages';
import externalDatabaseConnection from './modules/externalDatabaseConnection';
import globalConfig from 'shared/store/globalConfig';
import googleSheets from './modules/googleSheets';
import inboxAssignableAgents from './modules/inboxAssignableAgents';
import inboxes from './modules/inboxes';
import inboxMembers from './modules/inboxMembers';
import integrations from './modules/integrations';
import kanban from './modules/kanban';
import kanbanHandoffRules from './modules/kanbanHandoffRules';
import kanbanStageAutomations from './modules/kanbanStageAutomations';
import labels from './modules/labels';
import macros from './modules/macros';
import notifications from './modules/notifications';
import operationsDashboard from './modules/operationsDashboard';
import opsNotices from './modules/opsNotices';
import portals from './modules/helpCenterPortals';
import reports from './modules/reports';
import roundRobinReports from './modules/roundRobinReports';
import scheduledActions from './modules/scheduledActions';
import sla from './modules/sla';
import slaReports from './modules/SLAReports';
import summaryReports from './modules/summaryReports';
import teamMembers from './modules/teamMembers';
import teams from './modules/teams';
import userNotificationSettings from './modules/userNotificationSettings';
import webhooks from './modules/webhooks';
import whatsappAppTemplates from './modules/whatsappAppTemplates';
import captainAssistants from './captain/assistant';
import captainDocuments from './captain/document';
import captainResponses from './captain/response';
import captainInboxes from './captain/inboxes';
import captainBulkActions from './captain/bulkActions';
import copilotThreads from './captain/copilotThreads';
import copilotMessages from './captain/copilotMessages';
import captainScenarios from './captain/scenarios';
import captainTools from './captain/tools';
import asteriskTrunks from './modules/asteriskTrunks';
import asteriskExtensions from './modules/asteriskExtensions';
import asteriskQueues from './modules/asteriskQueues';
import asteriskQueueRoutes from './modules/asteriskQueueRoutes';
import asteriskDids from './modules/asteriskDids';
import asteriskHealth from './modules/asteriskHealth';
import asteriskIvrs from './modules/asteriskIvrs';
import callRecords from './modules/callRecords';
import ivrAudios from './modules/ivrAudios';
import ivrFlows from './modules/ivrFlows';
import whatsappSipExtensions from './modules/whatsappSipExtensions';
import whatsappSipQueues from './modules/whatsappSipQueues';
import whatsappSipDids from './modules/whatsappSipDids';
import whatsappSipTrunks from './modules/whatsappSipTrunks';
import whatsappSipIvrs from './modules/whatsappSipIvrs';
import whatsappSipTimeConditions from './modules/whatsappSipTimeConditions';
import whatsappSipHolidays from './modules/whatsappSipHolidays';
import whatsappSipSupervisor from './modules/whatsappSipSupervisor';
import whatsappSipAutodialer from './modules/whatsappSipAutodialer';
import whatsappSipCallEvents from './modules/whatsappSipCallEvents';
import telephonySupervisorPanel from './modules/telephonySupervisorPanel';
import studioAgents from './studio/agent';
import studioAnalytics from './studio/analytics';
import studioChamados from './studio/chamados';
import studioDocuments from './studio/document';
import studioFlows from './studio/flow';
import studioMassiva from './studio/massiva';
import studioMassivaMaintenance from './studio/massivaMaintenance';
import studioDataTables from './studio/dataTables';
import studioExternalTools from './studio/externalTool';
import studioProjects from './studio/project';
import studioResponses from './studio/response';
import studioTemplates from './studio/template';

const plugins = [
  createPersistedState({
    key: 'chatwoot-campaign-form',
    storage: window.sessionStorage,
    paths: [
      'campaigns.campaignData',
      'campaigns.campaignData.whatsappNormal',
      // ✅ Persistir dados do Google Sheets para campanhas
      'googleSheets.connection',
      'googleSheets.spreadsheets',
      'googleSheets.sheets',
      'googleSheets.columns',
      // ✅ Persistir draft do wizard AI campaigns entre steps
      'whatsappSipAutodialer.campaignDraft',
    ],
    filter(mutation) {
      return [
        // Campaigns mutations
        'campaigns/SET_CAMPAIGN_DATA',
        'campaigns/CLEAR_CAMPAIGN_DATA',
        'campaigns/SET_WHATSAPP_NORMAL_CAMPAIGN_DATA',
        'campaigns/CLEAR_WHATSAPP_NORMAL_CAMPAIGN_DATA',
        'campaigns/SET_WHATSAPP_NORMAL_CURRENT_STEP',
        // ✅ Google Sheets mutations
        'googleSheets/SET_GOOGLE_SHEETS_CONNECTION',
        'googleSheets/SET_GOOGLE_SHEETS_SPREADSHEETS',
        'googleSheets/SET_GOOGLE_SHEETS_SHEETS',
        'googleSheets/SET_GOOGLE_SHEETS_COLUMNS',
        'googleSheets/CLEAR_GOOGLE_SHEETS_DATA',
        // ✅ AI Campaign wizard draft
        'whatsappSipAutodialer/SAVE_DRAFT',
        'whatsappSipAutodialer/CLEAR_DRAFT',
      ].includes(mutation.type);
    },
  }),
];

export default createStore({
  modules: {
    accounts,
    adLeadsReport,
    agentBots,
    agents,
    articles,
    attributes,
    auditlogs,
    auth,
    automations,
    bulkActions,
    campaigns,
    cannedResponse,
    categories,
    contactConversations,
    contactLabels,
    contactNotes,
    contacts,
    conversationLabels,
    conversationMetadata,
    conversationPage,
    conversations,
    conversationSearch,
    conversationStats,
    conversationTypingStatus,
    conversationWatchers,
    csat,
    customRole,
    customViews,
    dashboardApps,
    draftMessages,
    externalDatabaseConnection,
    globalConfig,
    googleSheets,
    inboxAssignableAgents,
    inboxes,
    inboxMembers,
    integrations,
    kanban,
    kanbanHandoffRules,
    kanbanStageAutomations,
    labels,
    agentPauses,
    macros,
    notifications,
    operationsDashboard,
    opsNotices,
    portals,
    reports,
    roundRobinReports,
    scheduledActions,
    sla,
    slaReports,
    summaryReports,
    teamMembers,
    teams,
    userNotificationSettings,
    webhooks,
    whatsappAppTemplates,
    captainAssistants,
    captainDocuments,
    captainResponses,
    captainInboxes,
    captainBulkActions,
    copilotThreads,
    copilotMessages,
    captainScenarios,
    captainTools,
    asteriskTrunks,
    asteriskExtensions,
    asteriskQueues,
    asteriskQueueRoutes,
    asteriskDids,
    asteriskHealth,
    asteriskIvrs,
    callRecords,
    ivrAudios,
    ivrFlows,
    whatsappSipExtensions,
    whatsappSipQueues,
    whatsappSipDids,
    whatsappSipTrunks,
    whatsappSipIvrs,
    whatsappSipTimeConditions,
    whatsappSipHolidays,
    whatsappSipSupervisor,
    whatsappSipAutodialer,
    whatsappSipCallEvents,
    telephonySupervisorPanel,
    studioAgents,
    studioMassiva,
    studioMassivaMaintenance,
    studioDataTables,
    studioExternalTools,
    studioAnalytics,
    studioDocuments,
    studioChamados,
    studioFlows,
    studioProjects,
    studioResponses,
    studioTemplates,
  },
  plugins,
});
